import React, { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/en-ca';
import 'moment/locale/fr-ca';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement as ChartElement,
	Tooltip,
} from 'chart.js';
import { Line as Chart } from 'react-chartjs-2';

export interface IRecruiterChartDataPoint {
	key: string;
	count: number;
};

export interface IRecruiterChartProps {
	data: IRecruiterChartDataPoint[];
	labels?: {
		xAxis?: string;
		yAxis?: string;
	},
	onClick?: (dataPoint:IRecruiterChartDataPoint) => void;
}

const RecruiterChart: React.FC<IRecruiterChartProps> = ({
	data,
	labels,
	onClick,
}) => {
    const chartLanguage = document.documentElement.lang || 'en';
	const [ready,setReady] = useState(false);
    useEffect( () => {
        // Register required Chart.js elements
        ChartJS.register(
            CategoryScale,
            LinearScale,
            PointElement,
            ChartElement,
            Tooltip,
        );
		setReady(true);
    }, []);

	// Variables for linear gradient creation function
	let width;
	let height;
	let gradient;

    // Function to generate gradient for chart
	const getGradient = (ctx, chartArea) => {
		const chartWidth = chartArea.right - chartArea.left;
		const chartHeight = chartArea.bottom - chartArea.top;
		if (!gradient || width !== chartWidth || height !== chartHeight) {
			width = chartWidth;
			height = chartHeight;
			gradient = ctx.createLinearGradient(
				0,
				chartArea.bottom,
				0,
				chartArea.top,
			);
			gradient.addColorStop(0, '#007BC7');
			gradient.addColorStop(1, '#2308C9');
		}
		return gradient;
	};

	// Chart default options
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		borderWidth: 4,
		onClick: (event, elements) => {
			if (onClick && elements.length > 0) {
				onClick(data[elements[0].index]);
			}
		},
		plugins: {
			legend: {
				display: false,
			},
			title: {
				display: false,
			},
			tooltip: {
				backgroundColor: (context) => {
					const chart = context.chart;
					const { ctx, chartArea } = chart;

					if (!chartArea) {
						return;
					}
					return getGradient(ctx, chartArea);
				},
				cornerRadius: 16,
				caretSize: 8,
				padding: {
					top: 12,
					right: 16,
					bottom: 12,
					left: 16,
				},
				titleAlign: 'center',
				titleColor: '#FFFFFF',
				titleFont: {
					family: "'Plus Jakarta Sans', sans-serif",
					size: 12,
					weight: '400',
				},
				bodyAlign: 'center',
				bodyColor: '#FFFFFF',
				bodyFont: {
					family: "'Plus Jakarta Sans', sans-serif",
					size: 18,
					weight: '400',
				},
				displayColors: false,
			},
		},
		scales: {
			x: {
				title: {
					text: labels?.xAxis || '',
					display: false, // i think we don't need "Date" on the x-axis !! labels?.xAxis,
				},
				ticks: {
					font: {
						family: "'Plus Jakarta Sans', sans-serif",
						size: 12,
						weight: '400',
					},
					color: '#2E2E2E',
					maxRotation: 46,
					minRotation: 46,
					padding: 8,
				},
				grid: {
					display: false,
					borderColor: 'transparent',
				},
			},
			y: {
				type: 'linear',
				title: {
					text: labels?.yAxis || '',
					display: !! labels?.yAxis,
				},
				ticks: {
					font: {
						family: "'Plus Jakarta Sans', sans-serif",
						size: 12,
						weight: '400',
					},
					color: '#2E2E2E',
					padding: 8,
					precision: 0, // so it's integers on the scale
				},
				grid: {
					color: '#E9EBEC',
					borderColor: 'transparent',
				},
				suggestedMin: 0,
			},
		},
	};

    // Function to create individual chart data sources
    const createChartDataSource = (dataSet:{key:string,count:number}[]) => {
        if (chartLanguage === 'fr-ca') {
            moment.locale('fr-ca');
        } else {
            moment.locale('en-ca');
        }

        let dataSetXAxisLabels = [];
        let dataSetValues = [];

        dataSet.forEach((dataPoint) => {
            dataSetXAxisLabels.push(  moment(dataPoint.key).format('MMM D') );
            dataSetValues.push(dataPoint.count);
        });

        const dataSource = {
            labels: dataSetXAxisLabels,
            datasets: [
                {
                    data: dataSetValues,
                    lineTension: 0.3,
                    borderColor: function (context) {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;

                        if (!chartArea) {
                            return;
                        }
                        return getGradient(ctx, chartArea);
                    },
                    pointBackgroundColor: '#0574B9',
                    pointBorderColor: '#FFFAFA',
                    pointBorderWidth: 4,
                    pointRadius: 7,
                    pointHoverBorderWidth: 4,
                    pointHoverRadius: 7,
                    pointHoverBackgroundColor: '#ffffff',
                    pointHoverBorderColor: '#0574B9',
                },
            ],
        };

        return dataSource;
    };

	return ready ? <Chart
		// TS Ignore is used to prevent unnecessary problems being logged because of React-Chartjs-2 Typescript definitions
		// @ts-ignore
		options={chartOptions}
		data={createChartDataSource(data || [])}
	/> : null;
};

export default RecruiterChart;
